import { useElementOnView, useRouter } from '@capsicum/common-web';
import { type MarketplaceFilters, useAvailableMarketplaceMarkets } from '@capsicum/socios-core';
import { MarketFanTokenCard } from '@capsicum/socios-core-web';
import { memo, useRef } from 'react';
import styled from 'styled-components';

import { useBuySellButtonsForMarketplace } from '../../hooks';

const MarketFanTokenListSkeleton = ({ isPartnerWithMarketLoading }: { isPartnerWithMarketLoading: boolean }) => {
	return Array(5)
		.fill(0)
		.map((_, index) => (
			<MarketFanTokenCard
				isBuyEnabled={false}
				isBuyLoading={false}
				isLoading={isPartnerWithMarketLoading}
				isSellEnabled={false}
				isSellLoading={false}
				key={index}
				testID={`wallet-market-fan-tokens-${1}`}
			/>
		));
};

const MarketFanTokenList = ({ filters }: { filters: MarketplaceFilters }) => {
	const { push } = useRouter();
	const listEndRef = useRef<HTMLDivElement>(null);

	const { defaultCurrency, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isLoading, markets } =
		useAvailableMarketplaceMarkets(filters);

	const {
		handleBuyClick,
		handleSellClick,
		isBuyLoading,
		isFanTokenDirectPurchaseEnabled,
		isMarketplaceFanTokenBuyEnabled,
		isMarketplaceFanTokenSellEnabled,
		isSellLoading,
		tradeTokenId
	} = useBuySellButtonsForMarketplace();

	const cardHeight = 138;

	useElementOnView(
		listEndRef,
		({ isVisible }) => {
			if (isVisible && hasNextPage && !isFetching) fetchNextPage();
		},
		{ observerConfig: { rootMargin: `0px 0px ${5 * cardHeight}px 0px` } }
	);

	const handlePartnerLogoClick = (partnerId?: string) => {
		if (partnerId) push(`/partner/${partnerId}`);
	};

	return (
		<S.MarketList data-testid="wallet-market-list">
			{markets?.map(market => {
				// This is to prevent rendering whole list when buy or sell is clicked and only re-render the clicked market
				const isTokenSelectedForTrading = tradeTokenId === market?.baseCurrency?.id;

				return (
					<MarketFanTokenCard
						amount={market?.ticker?.lastPrice}
						currency={defaultCurrency}
						isBuyEnabled={
							isFanTokenDirectPurchaseEnabled && isMarketplaceFanTokenBuyEnabled && !!market?.isBuyAllowed
						}
						isBuyLoading={isTokenSelectedForTrading && isBuyLoading}
						isLoading={isLoading}
						isSellEnabled={isMarketplaceFanTokenSellEnabled && !!market?.isSellAllowed}
						isSellLoading={isTokenSelectedForTrading && isSellLoading}
						key={market.id}
						logo={market.logo}
						market={market}
						name={market.name}
						partnerId={market.partnerID}
						testID={`wallet-market-fan-tokens-${market.id}`}
						onClickBuy={handleBuyClick}
						onClickSell={handleSellClick}
						onPartnerLogoClick={handlePartnerLogoClick}
					/>
				);
			})}
			<div ref={listEndRef} />

			{(isFetchingNextPage || isLoading) && (
				<MarketFanTokenListSkeleton isPartnerWithMarketLoading={isFetchingNextPage || isLoading} />
			)}
		</S.MarketList>
	);
};

export const WalletMarketFanTokenList = memo(MarketFanTokenList, (prev, next) => {
	// This is to prevent re-rendering of the list when filters are not changed
	return (
		prev.filters?.currencyName === next.filters?.currencyName &&
		prev.filters?.sportIds === next.filters?.sportIds &&
		prev.filters?.partnerIds === next.filters?.partnerIds &&
		prev.filters?.orderBy === next.filters?.orderBy &&
		prev.filters?.direction === next.filters?.direction
	);
});

namespace S {
	export const MarketList = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xsmall};
		padding-bottom: ${({ theme }) => theme.spacings.xsmall};
		width: 100%;
	`;
}
